import { Routes, Route, Navigate } from 'react-router-dom';
import Home from './Home';
import TechnicalHomepage from './TechnicalHomepage';
import ApiDocumentation from './ApiDocumentation';
import SecurityPage from './SecurityPage';
import ClientImplementations from './ClientImplementations';
import Poc from './Poc';
import PlacingPortalPage from './PlacingPortalPage';
import PlacingPlacementPage from './PlacingPlacementPage';
import PlacingSubmissionPage from './PlacingSubmissionPage';
import PlacingQuoteRequestPage from './PlacingQuoteRequestPage';
import PlacingLineRequestPage from './PlacingLineRequestPage';
import PlacingSignedLineAdvicePage from './PlacingSignedLineAdvicePage';
import Admin from './Admin';
import Designer from './Designer';
import Auth0Callback from './Auth0Callback';
import NotFound from './NotFound';
import ProductHomepage from './ProductHomepage';
import Codex from "./Codex";
import Sources from "./Sources";
import CodifyHomepage from "./CodifyHomepage";
import Panner from "./Panner";
import GRLCPlacementEditorPage from "./GRLCPlacementEditorPage";
import GRLCPlacementEditorPageRefactored from "./GRLCPlacementEditorPageRefactored";
import PartyEditor from './PartyEditor';
import ProductDefinitionEditorPage from './ProductDefinitionEditorPage';
import DealEditorPage from './DealEditorPage';
import RegistersEditorPage from './RegistersEditorPage';
import RegisterEntriesEditorPage from './RegisterEntriesEditorPage';
import DocumentDefinitionsEditorPage from './DocumentDefinitionsEditorPage';
import PermissionDefinitionsEditorPage from './PermissionDefinitionsEditorPage';
import CodifyDefinitionsEditorPage from './CodifyDefinitionsEditorPage';
import CodifySpecificationsEditorPage from './CodifySpecificationsEditorPage';
import UsersEditorPage from './UsersEditorPage';
import ProtectedRoute from "./ProtectedRoute";
import SignalCreatorPage from "./SignalCreatorPage";
import SmartLedgersEditorPage from './SmartLedgersEditorPage';
import AccountsEditorPage from './AccountsEditorPage';
import JournalsEditorPage from './JournalsEditorPage';
import MyWorkflowDetailPage from './MyWorkflowDetailPage';
import JsonFormRenderer from './JsonFormRenderer';
import DocumentEditorLoader from './components/DocumentEditorLoader';
import DocumentEditorStandalone from './components/DocumentEditorStandalone';
import StudioHomepage from './StudioHomepage';
import PannerTreeBased from './components/PannerTreeBased';
import StudioDescription from './StudioDescription';
import CodifyDescription from './CodifyDescription';
import PannerDescription from './PannerDescription';
import OraclesDescription from './OraclesDescription';
import ContractOSDescription from './ContractOSDescription';
import RegistersDescription from './RegistersDescription';
import ProcessDescription from './ProcessDescription';

import "./index.css";   // ✅ this pulls in your CSS globally

const hostname = window.location.hostname;
const isPocEnv = hostname.startsWith('poc.');
const isStudioEnv = hostname.startsWith('studio.');

function App() {
  return (
    <Routes>
      {/* Dev redirect for case-sensitive URLs */}
      <Route path="/poc" element={<ProtectedRoute><Poc /></ProtectedRoute>} />
      <Route path="/placing" element={<ProtectedRoute><PlacingPortalPage /></ProtectedRoute>} />
      <Route path="/placing/inbox" element={<Navigate to="/placing?view=carrier" replace />} />
      <Route path="/placing/placements/:placementId" element={<ProtectedRoute><PlacingPlacementPage /></ProtectedRoute>} />
      <Route path="/placing/submissions/:submissionId" element={<ProtectedRoute><PlacingSubmissionPage /></ProtectedRoute>} />
      <Route path="/placing/quoterequests/:quoteRequestId" element={<ProtectedRoute><PlacingQuoteRequestPage /></ProtectedRoute>} />
      <Route path="/placing/linerequests/:lineRequestId" element={<ProtectedRoute><PlacingLineRequestPage /></ProtectedRoute>} />
      <Route path="/placing/signedlineadvices/:signedLineAdviceId" element={<ProtectedRoute><PlacingSignedLineAdvicePage /></ProtectedRoute>} />

      {/* Root route based on environment */}
      <Route
        path="/"
        element={
          isStudioEnv ? <Navigate to="/studio" replace /> :
          isPocEnv ? <Poc /> :
          <Home />
        }
      />

      {/* Functional routes */}
      <Route path="/technical" element={<ProtectedRoute><TechnicalHomepage /></ProtectedRoute>} />
      <Route path="/technical/api" element={<ProtectedRoute><ApiDocumentation /></ProtectedRoute>} />
      <Route path="/technical/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
      <Route path="/technical/clients" element={<ProtectedRoute><ClientImplementations /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/designer" element={<ProtectedRoute><Designer /></ProtectedRoute>} />
      <Route path="/product/:productCode" element={<ProtectedRoute><ProductHomepage /></ProtectedRoute>} />
      <Route path="/callback" element={<Auth0Callback />} />

      {/* Product description pages */}
      <Route path="/studio-info" element={<ProtectedRoute><StudioDescription /></ProtectedRoute>} />
      <Route path="/codify-info" element={<ProtectedRoute><CodifyDescription /></ProtectedRoute>} />
      <Route path="/panner-info" element={<ProtectedRoute><PannerDescription /></ProtectedRoute>} />
      <Route path="/oracles-info" element={<ProtectedRoute><OraclesDescription /></ProtectedRoute>} />
      <Route path="/contractos-info" element={<ProtectedRoute><ContractOSDescription /></ProtectedRoute>} />
      <Route path="/registers-info" element={<ProtectedRoute><RegistersDescription /></ProtectedRoute>} />
      <Route path="/process-info" element={<ProtectedRoute><ProcessDescription /></ProtectedRoute>} />

      {/* Studio routes - standalone document editor */}
      <Route path="/studio" element={<ProtectedRoute><StudioHomepage /></ProtectedRoute>} />
      <Route path="/editor" element={<ProtectedRoute><DocumentEditorStandalone /></ProtectedRoute>} />

      {/* Deal-based document editor */}
      <Route path="/editor/:productCode/:dealId" element={<ProtectedRoute><DocumentEditorLoader userRole="editor" /></ProtectedRoute>} />

      <Route path="/codex" element={<ProtectedRoute><Codex /></ProtectedRoute>} />
      <Route path="/sources" element={<ProtectedRoute><Sources /></ProtectedRoute>} />
      <Route path="/codify" element={<ProtectedRoute><CodifyHomepage /></ProtectedRoute>} />
      <Route path="/panner" element={<ProtectedRoute><Panner /></ProtectedRoute>} />
      <Route path="/panner-tree" element={<ProtectedRoute><PannerTreeBased /></ProtectedRoute>} />
      <Route path="/product/:productCode/deal/:dealId/placement" element={<ProtectedRoute><GRLCPlacementEditorPage /></ProtectedRoute>} />
      <Route path="/product/:productCode/deal/:dealId/placement-v2" element={<ProtectedRoute><GRLCPlacementEditorPageRefactored /></ProtectedRoute>} />
      <Route path="/product/:productCode/deal/:dealId/deal" element={<ProtectedRoute><DealEditorPage /></ProtectedRoute>} />
      <Route path="/product/:productCode/deal/:dealId/signal" element={<ProtectedRoute><SignalCreatorPage /></ProtectedRoute>} />
      <Route path="/party/:partyId" element={<ProtectedRoute><PartyEditor /></ProtectedRoute>} />
      <Route path="/registers/:registerId" element={<ProtectedRoute><RegistersEditorPage /></ProtectedRoute>} />
      <Route path="/registers/:registerId/entry/:entryId" element={<ProtectedRoute><RegisterEntriesEditorPage /></ProtectedRoute>} />
      <Route path="/documentdefinitions/:documentDefinitionId" element={<ProtectedRoute><DocumentDefinitionsEditorPage /></ProtectedRoute>} />
      <Route path="/permissiondefinitions/:permissionDefinitionId" element={<ProtectedRoute><PermissionDefinitionsEditorPage /></ProtectedRoute>} />
      <Route path="/codifydefinitions/:codifyDefinitionId" element={<ProtectedRoute><CodifyDefinitionsEditorPage /></ProtectedRoute>} />
      <Route path="/codifyspecifications/:codifySpecificationId" element={<ProtectedRoute><CodifySpecificationsEditorPage /></ProtectedRoute>} />
      <Route path="/smartledgers/:smartLedgerId" element={<ProtectedRoute><SmartLedgersEditorPage /></ProtectedRoute>} />
      <Route path="/accounts/:accountId" element={<ProtectedRoute><AccountsEditorPage /></ProtectedRoute>} />
      <Route path="/journals/:journalId" element={<ProtectedRoute><JournalsEditorPage /></ProtectedRoute>} />
      <Route path="/users/:userId" element={<ProtectedRoute><UsersEditorPage /></ProtectedRoute>} />
      <Route path="/poc/product/:productCode/edit" element={<ProtectedRoute><ProductDefinitionEditorPage /></ProtectedRoute>} />
      <Route path="/workflow/:id" element={<ProtectedRoute><MyWorkflowDetailPage baseUrl="https://workflow.poc.mrc.ai" /></ProtectedRoute>} />
      <Route path="/workflow/:id/payload" element={<ProtectedRoute><JsonFormRenderer /></ProtectedRoute>} />
      {/* 404 fallback */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;
